import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Paper, Typography } from '@mui/material';
import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useState } from 'react';

import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { getClientInfo } from '@selectors/client';
import { getTheme } from '@commons/utils/theme';

import { getMetricOptions } from './utils/config';
import ChartTooltip from '../common/ChartTooltip';
import RestaurantLimitedDropdown from '../common/RestaurantLimitedDropdown';

const GlobalStoreComparisonChart = ({
  selectedStores,
  globalTableData,
  enableTicketsNumber,
  isLoading,
  client: { storeName },
}) => {
  // États pour les filtres
  const metricOptions = getMetricOptions(enableTicketsNumber);
  const [selectedStoresForComparison, setSelectedStoresForComparison] = useState([]);
  const [selectedMetricForStoreComparison, setSelectedMetricForStoreComparison] = useState(
    metricOptions[0],
  );

  const theme = getTheme();

  // Préparer les données pour le graphique
  const prepareChartData = () => {
    if (!globalTableData || !selectedStoresForComparison.length) return [];

    return selectedStoresForComparison.map((store) => {
      const storeData = globalTableData.find((data) => data.storeName === store.name) || {};
      return {
        name: store.name,
        previous: storeData[selectedMetricForStoreComparison.id]?.previous,
        current: storeData[selectedMetricForStoreComparison.id]?.current,
      };
    });
  };

  const chartData = prepareChartData();

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, height: 420, border: '1px solid #E0E0E0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 420 }}>
          <DeepsightComponentLoader />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: 420, border: '1px solid #E0E0E0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('ANALYSIS.PERFORMANCE.STORE_COMPARISON', { storeName })}
        </Typography>

        {/* Filtres */}

        <Box sx={{ display: 'flex', gap: 2, '#dropdown-list': { maxHeight: '200px' } }}>
          {/* Dropdown Restaurants */}
          <RestaurantLimitedDropdown
            selectedStores={selectedStoresForComparison}
            setSelectedStores={setSelectedStoresForComparison}
            stores={selectedStores}
          />

          {/* Dropdown Métrique */}
          <Dropdown
            customStyle={{
              width: '200px',
            }}
            isRequired={true}
            isSearchDisabled={true}
            isUniqueSelection={true}
            items={metricOptions || []}
            label={i18next.t('ANALYSIS.PERFORMANCE.METRIC_DROPDOWN_LABEL')}
            selectedItem={selectedMetricForStoreComparison}
            onSelectionChange={setSelectedMetricForStoreComparison}
          />
        </Box>
      </Box>

      {/* Graphique */}
      {chartData.length > 0 ? (
        <ResponsiveContainer height={300} width="100%">
          <BarChart
            data={chartData}
            isLoading={isLoading}
            margin={{ bottom: 5, left: 20, right: 30, top: 20 }}
          >
            <CartesianGrid stroke="#EDF0F6" strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fill: '#6B768C', fontSize: 12 }} tickLine={false} />
            <YAxis axisLine={false} tick={{ fill: '#6B768C', fontSize: 12 }} tickLine={false} />
            <Tooltip
              content={
                <ChartTooltip
                  options={metricOptions}
                  selectedMetric={selectedMetricForStoreComparison}
                />
              }
              cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
            />
            <Legend />

            {/* Barre de référence (gris) */}
            <Bar
              dataKey="previous"
              fill={theme.colors.greys.medium}
              name={i18next.t('ANALYSIS.PERFORMANCE.PREVIOUS_PERIOD')}
              radius={[2, 2, 0, 0]}
            />

            {/* Barre actuelle (bleu Inpulse) */}
            <Bar
              dataKey="current"
              fill={theme.colors.blue}
              name={i18next.t('ANALYSIS.PERFORMANCE.CURRENT_PERIOD')}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography color="textSecondary" variant="body2">
            {i18next.t('GENERAL.NO_DATA_TO_DISPLAY')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps, null)(GlobalStoreComparisonChart);
